import React, { useState, useEffect } from "react";
import DashboardNav from "../DashboardNavbar/DashboardNav";
import delete_icon from "../../assets/img/delete_icon.png";
import add_icon from "../../assets/img/add_icon.png";

import { getAllUsers, deleteUser } from "../../api/Api";
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Paper,
  Skeleton,
  TablePagination,
  Select,
  MenuItem,
} from "@mui/material";

import { useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import AddUserModal from "../../components/modal/AddUserModal";
import UpdateUserModal from "../../components/modal/UpdateUserModal";
import UniversalModal from "../../components/modal/UniversalModal";

const CustomerDashboard = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [uniqueAreas, setUniqueAreas] = useState([]);

  const [searchUser, setSearchUser] = useState("");
  const [location, setLocation] = useState("");
  const [monthlyNewUser, setMonthlyNewUser] = useState("");
  const [openAddUserModal, setOpenAddUserModal] = useState(false);
  const [openUpdateModal, setOpenUpdateModal] = useState(false);
  const [deletingUserId, setDeletingUserId] = useState(null); // Tracks the ID of the user being deleted

  const [isUniversalModalOpen, setisUniversalModalOpen] = useState(false);

  //modal message state
  const [info, setInfo] = useState("");

  // Pagination state
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const [selectedUser, setSelectedUser] = useState({
    userId: "",
    userName: "",
    emailId: "",
    contactNo: "",
    idProof: "",
    deliveryAddress: "",
  });

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await getAllUsers();
      // Filter users with the role of "customer"
      const customers = response.data.user.filter(
        (user) => user.role === "customer"
      );
      setUsers(customers); // Set only customers in the state
      setTotalUsers(customers.length);
      const areas = [...new Set(customers.map((user) => user.area))];
      setUniqueAreas(areas);
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    setDeletingUserId(id); // Start deleting process
    try {
      await deleteUser(id);
      setUsers((prevUsers) => prevUsers.filter((user) => user.userId !== id));
      setInfo("User deleted successfully");
      setisUniversalModalOpen(true);
      fetchUsers();
    } catch (error) {
      console.error("Error deleting user:", error);
    } finally {
      setDeletingUserId(null); // Reset deleting state
    }
  };

  const handleOpenUpdateModal = (user) => {
    setSelectedUser(user);
    setOpenUpdateModal(true);
  };

  const handleCloseUpdateModal = () => {
    setOpenUpdateModal(false);
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleUpdateUser = (updatedUserData) => {
    const isDataUpdated = !users.some(
      (user) =>
        user.name === updatedUserData.name &&
        user.email === updatedUserData.email &&
        user.phone === updatedUserData.phone &&
        user.area === updatedUserData.selectedArea
    );

    if (isDataUpdated) {
      // Open universal modal if data is new
      setInfo("User data has been updated successfully.");
      setisUniversalModalOpen(true);

      // Fetch users after the update
      fetchUsers();
    }

    setOpenUpdateModal(false);
  };

  const handleOpenAddUserModal = () => {
    setOpenAddUserModal(true);
  };

  const handleCloseAddUserModal = () => {
    setOpenAddUserModal(false);
  };

  const handleAddUserSubmit = (userData) => {
    // Check if the submitted userData already exists in the current user list

    const isDataUpdated = !users.some(
      (user) =>
        user.name === userData.name &&
        user.email === userData.email &&
        user.phone === userData.phone &&
        user.area === userData.selectedArea
    );

    if (isDataUpdated) {
      // Open universal modal if data is new
      setInfo("User data has been added successfully.");
      setisUniversalModalOpen(true);
    }

    fetchUsers(); // Refresh the user list
    // console.log("User data submitted:", userData);
  };

  const handleSearchUserChange = (e) => setSearchUser(e.target.value);
  const handleLocationChange = (e) => setLocation(e.target.value);

  const filteredusers = users.filter((user) => {
    const matchesSearchUser =
      searchUser === "" ||
      user.name.toLowerCase().includes(searchUser.toLowerCase());
    const matchesLocation = location === "" || user.area === location;

    return matchesSearchUser && matchesLocation;
  });

  const handlePageChange = (event, newPage) => setCurrentPage(newPage);

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setCurrentPage(0); // Reset to first page when rows per page changes
  };

  const paginatedUsers = filteredusers.slice(
    currentPage * rowsPerPage,
    currentPage * rowsPerPage + rowsPerPage
  );
  const handleUniversalModal = () => {
    setisUniversalModalOpen(false);
  };

  return (
    <Box sx={{ height: "100vh" }}>
      {/* Navbar */}
      <Box sx={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: 1000 }}>
        <DashboardNav />
      </Box>

      {/* Main content */}
      <Box
        sx={{
          position: "absolute",
          top: "180px",
          left: 0,
          right: 0,
          px: 1,
          width: "90vw",
          margin: "auto",
          color: "#888585",
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontWeight: "500",
            fontSize: "32px",
            fontFamily: theme.typography.fontFamily,
          }}
          gutterBottom
        >
          Customers
        </Typography>

        {/* Transaction Cards */}
        <Grid container spacing={1}>
          {/* Card 1 */}
          <Grid item xs={12} md={4}>
            <Card
              sx={{
                background: "linear-gradient(180deg, #EA4245 0%, #F5FFFB 100%)",
                borderRadius: "10px",
                color: "#F5F5F5",
                width: "80%",
                padding: "20px",
                boxShadow: "0px 4px 4px 0px #E59292", // Box shadow
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between", // Align content in one row
                height: "100px", // Set fixed height
              }}
            >
              <CardContent
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  width: "100%",
                }}
              >
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{
                    fontWeight: "500",
                    fontSize: "24px", // Increase font size
                    color: "#843838", // Apply color
                  }}
                >
                  Total no of Users
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: "500",
                    fontSize: "36px",
                  }}
                >
                  {totalUsers}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          {/* Card 2 */}
          <Grid item xs={12} md={4}>
            <Card
              sx={{
                background: "linear-gradient(180deg, #1A6714 0%, #F5FFFB 100%)", // Linear gradient
                borderRadius: "10px",
                color: "#F5F5F5",
                width: "80%",
                padding: "20px",
                boxShadow: "0px 4px 4px 0px #9AEFB6", // Box shadow
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between", // Align content in one row
                height: "100px", // Set fixed height
              }}
            >
              <CardContent
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  width: "100%",
                }}
              >
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{
                    fontWeight: "500",
                    fontSize: "24px", // Increase font size
                    color: "#1A6714", // Apply text color
                  }}
                >
                  Paylater Users
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: "500",
                    fontSize: "36px",
                  }}
                >
                  0
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Card 3 */}
          <Grid item xs={12} md={4}>
            <Card
              sx={{
                background: "linear-gradient(180deg, #4030D2 0%, #F5FFFB 100%)", // Linear gradient
                borderRadius: "10px",
                color: "#FFFFFF",
                width: "80%",
                padding: "20px",
                boxShadow: "0px 4px 4px 0px #DFD8FF", // Box shadow
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between", // Align content in one row
                height: "100px", // Set fixed height
              }}
            >
              <CardContent
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  width: "100%",
                }}
              >
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{
                    fontWeight: "500",
                    fontSize: "24px", // Increase font size
                    color: "#3D2C8B",
                    lineHeight: "34.85px",
                  }}
                >
                  Monthly New Users
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: "500",
                    fontSize: "36px",
                  }}
                >
                  0
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        {/* Customer Details */}
        <Box sx={{ mt: 4, color: "#888585" }}>
          <Grid
            item
            xs={12}
            md={12}
            sx={{ textAlign: "right", marginRight: "5vw" }}
          ></Grid>
          <Typography
            variant="h4"
            sx={{
              fontWeight: "500",
              fontSize: "32px",
              fontFamily: theme.typography.fontFamily,
            }}
            gutterBottom
          >
            User Details
          </Typography>

          {/* Filter Fields */}
          <Grid
            container
            spacing={2}
            sx={{ mb: 2 }}
            justifyContent="space-between"
          >
            <Grid item xs={12} md={9}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Search User"
                    variant="outlined"
                    value={searchUser}
                    onChange={handleSearchUserChange}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Select
                    fullWidth
                    displayEmpty
                    value={location}
                    onChange={handleLocationChange}
                  >
                    <MenuItem value="">All Locations</MenuItem>
                    {uniqueAreas.map((area) => (
                      <MenuItem key={area} value={area}>
                        {area}
                      </MenuItem>
                    ))}
                  </Select>
                </Grid>
              </Grid>
            </Grid>
            <Grid
              item
              xs={12}
              md={3}
              sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}
            >
              <Button
                variant="contained"
                sx={{
                  backgroundColor: theme.palette.primary.light,
                  width: "10vw",
                  color: "#fff",
                  "&:hover": {
                    backgroundColor: theme.palette.primary.main,
                  },
                  textTransform: "capitalize",
                }}
                startIcon={
                  <img
                    src={add_icon}
                    alt="Add"
                    style={{
                      width: "24px",
                      height: "24px",
                      objectFit: "contain",
                    }}
                  />
                }
                onClick={handleOpenAddUserModal} // Open the modal when clicked
              >
                Add User
              </Button>
              <AddUserModal
                open={openAddUserModal}
                handleClose={handleCloseAddUserModal}
                handleSubmit={handleAddUserSubmit}
              />
            </Grid>
          </Grid>

          {/* users Table */}
          <TableContainer component={Paper} sx={{ mt: 4 }}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: theme.palette.primary.light, p: 4 }}>
                  <TableCell sx={{ color: "#fff", textAlign: "center" }}>
                    Sl No
                  </TableCell>
                  <TableCell sx={{ color: "#fff", textAlign: "center" }}>
                    User Id
                  </TableCell>
                  <TableCell sx={{ color: "#fff", textAlign: "center" }}>
                    User Name
                  </TableCell>
                  <TableCell sx={{ color: "#fff", textAlign: "center" }}>
                    Delivery Address
                  </TableCell>
                  <TableCell sx={{ color: "#fff", textAlign: "center" }}>
                    Contact No
                  </TableCell>
                  <TableCell sx={{ color: "#fff", textAlign: "center" }}>
                    Email Id
                  </TableCell>
                  <TableCell sx={{ color: "#fff", textAlign: "center" }}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  Array.from(new Array(rowsPerPage)).map((_, index) => (
                    <TableRow key={index}>
                      {[50, 80, 100, 150, 80, 100, 100].map((width, idx) => (
                        <TableCell key={idx} align="center">
                          <Skeleton variant="text" width={width} />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : paginatedUsers.length > 0 ? (
                  [...paginatedUsers].reverse().map((user, index) => (
                    <TableRow key={user._id}>
                      <TableCell align="center">
                        {currentPage * rowsPerPage + index + 1}
                      </TableCell>
                      <TableCell align="center">
                        {user._id.slice(0, 3)}
                      </TableCell>
                      <TableCell align="center">{user.name}</TableCell>
                      <TableCell align="center">
                        {user.area ? <p>{user.area}</p> : "N/A"}
                      </TableCell>
                      <TableCell align="center">{user.phone}</TableCell>
                      <TableCell align="center">{user.email}</TableCell>
                      <TableCell align="center">
                        <Button
                          variant="outlined"
                          sx={{
                            color: "#ffffff",
                            backgroundColor: "#FF893A",
                            "&:hover": { backgroundColor: "#FF879A" },
                            marginRight: 1,
                            textTransform: "capitalize",
                          }}
                          onClick={() => handleOpenUpdateModal(user)}
                        >
                          Update
                        </Button>
                        {selectedUser && (
                          <UpdateUserModal
                            open={openUpdateModal}
                            handleClose={handleCloseUpdateModal}
                            userData={selectedUser}
                            handleUpdateUser={handleUpdateUser}
                          />
                        )}
                        <Button
                          variant="outlined"
                          sx={{
                            color: "#ffffff",
                            backgroundColor: theme.palette.primary.light,
                            "&:hover": { backgroundColor: theme.palette.primary.main },
                            marginRight: 1,
                            textTransform: "capitalize",
                          }}
                          onClick={() =>
                            navigate("/OrderHistory", {
                              state: { rowData: user },
                            })
                          }
                        >
                          Order History
                        </Button>
                        <Button
                          variant="outlined"
                          sx={{
                            color: "#fff",
                            backgroundColor: "#FF5252",
                            "&:hover": { backgroundColor: "#FF1744" },
                            textTransform: "capitalize",
                          }}
                          onClick={() => handleDelete(user._id)}
                          disabled={deletingUserId === user._id}
                        >
                          {deletingUserId === user._id ? (
                            "Deleting..."
                          ) : (
                            <img
                              src={delete_icon}
                              alt="Delete"
                              style={{ width: "22px", height: "22px" }}
                            />
                          )}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      No users available.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 15,45,55,75,100,200,500]}
            component="div"
            count={filteredusers.length}
            rowsPerPage={rowsPerPage}
            page={currentPage}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
          />
        </Box>
      </Box>
      <UniversalModal
        open={isUniversalModalOpen}
        onClose={handleUniversalModal}
        message={info}
      />
    </Box>
  );
};

export default CustomerDashboard;
